// FeedItemCard.tsx — one item in the self-advancing recovery feed.
// The current item is featured (poster, progress, prominent action); upcoming
// items are compact and peek below it. Every card can be favourited.
import type { Ref } from 'react';
import { Display, Body, Meta, Card, Plate, GlyphWell, PlayDisc, StatePill, Pressable, fmt } from './ui';
import { Icon, type IconName } from './icons';
import { FavouriteButton } from './FavouriteButton';
import { StepHero } from './StepHero';
import type { Module, Step } from '@/types';
import { VARIANT_TILES, NEW_UI } from '@/config/uiFlags';
import { useIsDesktop } from '@/hooks/useIsDesktop';

const KIND: Record<string, { icon: IconName; label: string; action: string }> = {
  video: { icon: 'film', label: 'Video', action: 'Watch' },
  article: { icon: 'book', label: 'Reading', action: 'Read' },
  reflection: { icon: 'pencil', label: 'Journalling', action: 'Reflect' },
  exercise: { icon: 'leaf', label: 'Exercise', action: 'Begin' },
  audio: { icon: 'audio', label: 'Audio', action: 'Listen' },
  mixed: { icon: 'sparkle', label: 'Step', action: 'Open' },
};

const AHA_KIND: { icon: IconName; label: string; action: string } = {
  icon: 'sparkle',
  label: 'Aha moment',
  action: 'Capture',
};

function meta(step: Step): string {
  if (step.type === 'video' && step.duration) return `${Math.round(step.duration / 60)} min`;
  if (step.type === 'article' && step.read_minutes) return `${step.read_minutes} min read`;
  if (step.type === 'reflection') return '2 min';
  if (step.type === 'audio' && step.duration) return `${Math.round(step.duration / 60)} min`;
  return '';
}

function preview(step: Step): string | undefined {
  if (step.blurb) return step.blurb;
  if (step.lede) return step.lede;
  if (step.reflection_prompt) return step.reflection_prompt;
  return undefined;
}

export function FeedItemCard({
  step,
  featured,
  locked,
  showStartHint,
  position,
  onOpen,
  onComplete,
  cardRef,
}: {
  step: Step;
  /** Still accepted by callers; the chapter eyebrow it fed has been removed. */
  module?: Module;
  featured: boolean;
  /** Faded, not yet openable — waiting its turn in the module. */
  locked?: boolean;
  /** First-ever video: overlay a "Tap to start" teaching cue on the play disc. */
  showStartHint?: boolean;
  position: number;
  onOpen: (step: Step) => void;
  /** Complete a step inline (used by image-only lessons that need no viewer). */
  onComplete?: (step: Step) => void;
  cardRef?: Ref<HTMLDivElement>;
}) {
  const kind = step.aha ? AHA_KIND : KIND[step.type] ?? KIND.mixed;
  const isVideo = step.type === 'video';
  // On desktop the reading column is wide, so full-bleed lesson graphics balloon.
  // Cap + centre them on desktop only; phones are unaffected (never match 1024px).
  const isDesktop = useIsDesktop();
  const hasText = !!((step.body && step.body.length) || step.body_md || step.lede || step.reflection_prompt);
  // Lessons whose whole content is the image: show it inline in the tile (no
  // viewer) with a small Next button. Other image cards use the poster treatment
  // (image behind a dark mask, title on top) so image + title never overlap.
  const imageOnlyTile = !!step.image_url && !hasText;
  const imageFull = !!step.image_url && hasText;
  const titleStr = `${step.title.pre}${step.title.em || ''}`;
  const duration = step.duration ?? 0;
  const inProgress = isVideo && position > 0 && position < duration;
  const pct = inProgress ? Math.min(1, position / duration) : 0;
  const action = isVideo ? (inProgress ? 'Resume' : 'Watch') : kind.action;

  // scroll-margin-top lets the feed land this card just below the fixed header.
  const wrap = { scrollMarginTop: 'calc(var(--safe-top) + var(--frame-header-h) + 10px)' } as const;

  // Waiting (upcoming) tiles get a full-width cover-image treatment (reversible
  // via VARIANT_TILES). The current/featured "preview" tile keeps its original
  // design (hero poster + blurb + action), handled by the blocks below.
  const useVariant = VARIANT_TILES && !featured;
  if (useVariant) {
    return (
      <div ref={cardRef} style={wrap}>
        <VariantTile step={step} kind={kind} locked={locked} metaText={meta(step)} onOpen={onOpen} />
      </div>
    );
  }

  // Full-width image lesson, current: show the image right in the tile with a
  // small Next button — no viewer step.
  if (featured && imageOnlyTile) {
    return (
      <div ref={cardRef} style={wrap}>
        {/* recommended-action card: the deepest elevation in the app (active),
            reserved for "this matters most". Keeps the level-2 gradient. */}
        <Card level={2} glow pad={0} style={{ background: 'var(--recommend-grad)', boxShadow: 'var(--edge-light), var(--lift-glow)', border: '1px solid rgba(154,122,51,0.30)' }}>
          {/* image fills the tile edge-to-edge (no white padding) so its text is
              as large/legible as possible without a viewer */}
          <div style={{ position: 'relative', background: '#fff' }}>
            <img src={step.image_url} alt={titleStr} decoding="async" style={{ display: 'block', width: '100%', maxWidth: isDesktop ? 460 : undefined, margin: isDesktop ? '0 auto' : undefined, objectFit: 'contain' }} />
            <div style={{ position: 'absolute', top: 10, right: 10 }}>
              <FavouriteButton stepId={step.id} />
            </div>
          </div>
          <div style={{ padding: '12px 16px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <Display parts={step.title} size={16} lh={1.16} style={{ flex: 1, minWidth: 0 }} />
            <Pressable
              as="button"
              onClick={() => (onComplete ?? onOpen)(step)}
              ariaLabel="Next"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                flexShrink: 0,
                padding: '9px 15px',
                borderRadius: 999,
                border: 'none',
                background: 'var(--green-deep)',
                color: '#fff',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 13.5,
                fontWeight: 600,
                minHeight: 40,
              }}
            >
              Next <Icon name="chevRight" size={15} sw={2.4} />
            </Pressable>
          </div>
        </Card>
      </div>
    );
  }

  if (featured) {
    return (
      <div ref={cardRef} style={wrap}>
        {/* recommended-action card: deepest elevation (active) — the single most
            important thing on Today. Reserved use; keeps the level-2 gradient. */}
        <Card level={2} glow pad={0} onClick={() => onOpen(step)} style={{ background: 'var(--recommend-grad)', boxShadow: 'var(--edge-light), var(--lift-glow)', border: '1px solid rgba(154,122,51,0.30)' }}>
          {isVideo ? (
            <Plate
              ratio="16 / 8.4"
              radius={0}
              poster={step.poster_url}
              caption="evening light · a quiet room"
              overlay={
                <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', padding: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                    <FavouriteButton stepId={step.id} onLight />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <StatePill state="current" onLight />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {showStartHint && (
                        <>
                          <style>{`@keyframes rnTapPulse{0%{transform:scale(1);opacity:.85}70%{transform:scale(1.45);opacity:0}100%{opacity:0}}@keyframes rnTapNudge{0%,100%{transform:translateX(0)}50%{transform:translateX(4px)}}`}</style>
                          <span
                            aria-hidden="true"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '7px 12px',
                              borderRadius: 999,
                              background: 'rgba(255,250,240,0.96)',
                              color: 'var(--ink)',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              fontSize: 12.5,
                              fontWeight: 700,
                              letterSpacing: '0.01em',
                              boxShadow: '0 4px 14px rgba(0,0,0,0.3)',
                              animation: 'rnTapNudge 1.7s ease-in-out infinite',
                            }}
                          >
                            Tap to start
                            <Icon name="chevRight" size={14} sw={2.6} />
                          </span>
                        </>
                      )}
                      <div style={{ position: 'relative', display: 'flex' }}>
                        {showStartHint && (
                          <span
                            aria-hidden="true"
                            style={{
                              position: 'absolute',
                              inset: -5,
                              borderRadius: 999,
                              border: '2.5px solid rgba(255,250,240,0.95)',
                              animation: 'rnTapPulse 1.7s ease-out infinite',
                              pointerEvents: 'none',
                            }}
                          />
                        )}
                        <PlayDisc
                          size={48}
                          onImage
                          label="Resume step"
                          onClick={(ev) => {
                            ev.stopPropagation();
                            onOpen(step);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              }
            />
          ) : (
            <StepHero
              type={step.type}
              aha={step.aha}
              image={step.image_url}
              imageFull={imageFull}
              stepId={step.id}
              metaText={meta(step) || undefined}
            />
          )}
          <div style={{ padding: '15px 16px 16px' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                marginBottom: 7,
                color: 'var(--amber-deep)',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}
            >
              <Icon name={kind.icon} size={13} /> {kind.label}
              {meta(step) ? ` · ${meta(step)}` : ''}
              {inProgress ? ` · ${fmt(Math.max(0, duration - position))} left` : ''}
            </div>
            <Display parts={step.title} size={20} lh={1.16} />
            {preview(step) && (
              <Body dim size={13.5} style={{ marginTop: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {preview(step)}
              </Body>
            )}
            {inProgress && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 13 }}>
                <div style={{ flex: 1, height: 5, borderRadius: 999, background: 'var(--paper)', boxShadow: 'inset 0 1px 2px rgba(74,52,24,0.14)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: pct * 100 + '%', background: 'linear-gradient(90deg, var(--amber), #E4AE50)', borderRadius: 999 }} />
                </div>
                <Meta style={{ fontVariantNumeric: 'tabular-nums' }}>{Math.round(pct * 100)}%</Meta>
              </div>
            )}
            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--green-deep)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13.5, fontWeight: 600 }}>
              <Icon name={isVideo ? 'play' : kind.icon} size={15} /> {action}
              <span style={{ flex: 1 }} />
              <span style={{ color: 'var(--ink-soft)', opacity: 0.4 }}>
                <Icon name="chevRight" size={16} />
              </span>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // compact upcoming item (locked = faded, waits its turn)
  const tone = step.type === 'article' ? 'amber' : step.type === 'reflection' ? 'green' : 'amber';
  const color = step.type === 'reflection' ? 'var(--green-deep)' : 'var(--amber-deep)';
  return (
    <div ref={cardRef} style={wrap}>
      <Card pad={14} onClick={locked ? undefined : () => onOpen(step)} style={{ background: 'var(--card-raised-grad)', boxShadow: 'var(--edge-light), var(--lift-soft)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
          <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 13, opacity: locked ? 0.5 : 1 }}>
            {isVideo && !locked ? (
              <PlayDisc size={44} variant="green" label="Watch step" onClick={(ev) => { ev.stopPropagation(); onOpen(step); }} />
            ) : (
              <GlyphWell size={44} radius={13} tone={tone} color={color}>
                <Icon name={kind.icon} size={20} />
              </GlyphWell>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--ink-soft)',
                  marginBottom: 3,
                }}
              >
                <span>
                  {kind.label}
                  {meta(step) ? ` · ${meta(step)}` : ''}
                </span>
                {locked && <span style={{ opacity: 0.7 }}>· next in turn</span>}
              </div>
              <Display parts={step.title} size={16} lh={1.2} />
            </div>
          </div>
          <FavouriteButton stepId={step.id} />
        </div>
      </Card>
    </div>
  );
}

// Waiting (upcoming) tiles come in TWO looks, mixed down the list for variety:
//   • CoverTile — full-width cover image, title overlaid
//   • SideTile  — image-left (~36%) panel, label + title on the right
// Which look a lesson gets is fixed by a hash of its id (stable as it scrolls
// up), so the list alternates without a rigid pattern. The current/featured
// "preview" tile is unchanged (handled in FeedItemCard above).
const variantOf = (step: Step) =>
  step.aha ? 'aha' : step.type === 'video' ? 'video' : step.type === 'reflection' ? 'reflection' : 'lesson';

const COVER_GRADIENT: Record<string, string> = {
  lesson: 'linear-gradient(150deg, #C99A4E, #8C5E12)',
  reflection: 'linear-gradient(150deg, #5E8E76, #39614C)',
  aha: 'linear-gradient(150deg, #C8923B, #9C6C1C)',
  video: 'linear-gradient(150deg, #C99A4E, #8C5E12)',
};
const SIDE_ACCENT: Record<string, string> = {
  lesson: 'var(--amber-deep)',
  reflection: 'var(--green-deep)',
  aha: '#B6852F',
  video: 'var(--amber-deep)',
};
const SIDE_PANEL: Record<string, string> = {
  lesson: 'linear-gradient(150deg, #F4E4C6, #E7CF9F)',
  video: 'linear-gradient(150deg, #E9DAC0, #D9C39C)',
};
const LINED_PAPER =
  'repeating-linear-gradient(180deg, var(--paper), var(--paper) 10px, rgba(74,52,24,0.08) 10px, rgba(74,52,24,0.08) 11px)';

// stable per-id hash → pick which of the two looks a lesson uses
const layoutHash = (id: string) => {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
};

// Calming fallback covers, so a tile is never a bare brown/empty panel when its
// lesson has no image of its own. Picked deterministically by id so each tile
// keeps the same image as it scrolls.
const FALLBACK_COVERS = [
  '/quick-resets/ground.webp',
  '/quick-resets/orienting.webp',
  '/quick-resets/sensory.webp',
  '/quick-resets/movement.webp',
  '/quick-resets/exhale.webp',
  '/quick-resets/muscle.webp',
];
export const coverFor = (step: Step): string => {
  const direct = step.type === 'video' ? step.poster_url : step.image_url;
  if (direct) return direct;
  // Journalling/reflection tiles carry NO photo. They show the app's own paper-
  // world placeholder instead — the lined-paper panel + pencil in SideTile, or the
  // green gradient in CoverTile — matching StepHero on the featured tile. (Returning
  // a photo here is what wrongly painted Calm-Your-Body / journal stock over them.)
  if (step.type === 'reflection') return '';
  return FALLBACK_COVERS[layoutHash(step.id) % FALLBACK_COVERS.length];
};

type TileProps = {
  step: Step;
  kind: { icon: IconName; label: string; action: string };
  locked?: boolean;
  metaText: string;
  onOpen: (step: Step) => void;
};

function VariantTile(props: TileProps) {
  // Videos always get the full-width cover (never the small image-left look),
  // so a video tile reads like "But Is It Really Abuse?" every time.
  if (props.step.type === 'video') return <CoverTile {...props} />;
  return layoutHash(props.step.id) % 2 === 0 ? <CoverTile {...props} /> : <SideTile {...props} />;
}

function CoverTile({ step, kind, locked, metaText, onOpen }: TileProps) {
  const isVideo = step.type === 'video';
  const variant = variantOf(step);
  const cover = coverFor(step);
  const clickable = !locked;
  const title = `${step.title.pre}${step.title.em || ''}`;

  return (
    <div
      onClick={clickable ? () => onOpen(step) : undefined}
      role={clickable ? 'button' : undefined}
      style={{
        position: 'relative',
        borderRadius: 16,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'flex-end',
        minHeight: 138,
        background: COVER_GRADIENT[variant],
        border: variant === 'aha' ? '1px solid rgba(212,160,70,0.55)' : '1px solid var(--hair)',
        boxShadow:
          variant === 'aha'
            ? '0 0 0 1px rgba(212,160,70,0.35), 0 8px 24px -8px rgba(212,160,70,0.5), var(--sh1)'
            : 'var(--edge-light), var(--lift-soft)',
        cursor: clickable ? 'pointer' : 'default',
        opacity: locked ? 0.6 : 1, // upcoming tiles wash out so the live tile is unambiguous
        transition: 'opacity .3s ease',
      }}
    >
      {cover && (
        <img
          src={cover}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
      )}
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(20,14,8,0) 38%, rgba(20,14,8,0.64))',
        }}
      />
      {isVideo && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '46%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 48,
            height: 48,
            borderRadius: 999,
            background: 'rgba(255,250,240,0.92)',
            color: 'var(--amber-deep)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.32)',
          }}
        >
          <Icon name="play" size={20} />
        </span>
      )}
      {variant === 'aha' && (
        <span style={{ position: 'absolute', top: 9, left: 10, color: '#FBE7B6' }}>
          <Icon name="sparkle" size={15} />
        </span>
      )}
      <div style={{ position: 'absolute', top: 8, right: 8 }}>
        <FavouriteButton stepId={step.id} onLight />
      </div>
      <div style={{ position: 'relative', zIndex: 2, padding: '12px 14px 13px', width: '100%' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'rgba(255,248,236,0.92)',
            fontFamily: 'Inter, system-ui, sans-serif',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            textShadow: '0 1px 4px rgba(0,0,0,0.5)',
            marginBottom: 5,
          }}
        >
          <Icon name={kind.icon} size={12} />
          {kind.label}
          {metaText ? ` · ${metaText}` : ''}
          {locked ? ' · next in turn' : ''}
        </div>
        <div
          style={{
            fontFamily: 'Fraunces, Georgia, serif',
            fontSize: 17,
            fontWeight: 500,
            lineHeight: 1.18,
            color: '#FFF7EA',
            textShadow: '0 1px 6px rgba(0,0,0,0.5)',
          }}
        >
          {title}
        </div>
      </div>
      {/* locked → a soft grey veil so upcoming tiles read as recessed/waiting
          on the cream background */}
      {locked && (
        <span
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, zIndex: 4, background: 'rgba(247,245,240,0.4)', pointerEvents: 'none' }}
        />
      )}
    </div>
  );
}

function SideTile({ step, kind, locked, metaText, onOpen }: TileProps) {
  const isVideo = step.type === 'video';
  const variant = variantOf(step);
  const accent = SIDE_ACCENT[variant];
  const cover = coverFor(step);
  const clickable = !locked;
  const lined = variant === 'reflection' || variant === 'aha';

  return (
    <div
      onClick={clickable ? () => onOpen(step) : undefined}
      role={clickable ? 'button' : undefined}
      style={{
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        borderRadius: 16,
        overflow: 'hidden',
        // NEW_UI: lighter, warmer tile body so the feed doesn't read dark against the
        // rest of the app. Live (flag off) keeps the original card gradient.
        background: NEW_UI ? 'linear-gradient(180deg, #FCF8F0, #F5EEE1)' : 'linear-gradient(180deg, var(--card-top), var(--card-bot))',
        border: variant === 'aha' ? '1px solid rgba(212,160,70,0.55)' : '1px solid var(--hair)',
        boxShadow:
          variant === 'aha'
            ? '0 0 0 1px rgba(212,160,70,0.4), 0 8px 26px -8px rgba(212,160,70,0.55), var(--sh1)'
            : 'var(--edge-light), var(--lift-soft)',
        cursor: clickable ? 'pointer' : 'default',
        minHeight: 96,
        opacity: locked ? 0.6 : 1, // upcoming tiles wash out so the live tile is unambiguous
        transition: 'opacity .3s ease',
      }}
    >
      {/* left image panel (~36%) */}
      <div
        style={{
          position: 'relative',
          flex: '0 0 36%',
          maxWidth: 150,
          minWidth: 104,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: lined ? LINED_PAPER : SIDE_PANEL[variant],
        }}
      >
        {cover && (
          <>
            <img
              src={cover}
              alt=""
              aria-hidden="true"
              loading="lazy"
              decoding="async"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
            />
            <span
              aria-hidden="true"
              style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(20,14,8,0) 55%, rgba(20,14,8,0.14))' }}
            />
          </>
        )}
        {isVideo ? (
          <span
            aria-hidden="true"
            style={{
              position: 'relative',
              width: 36,
              height: 36,
              borderRadius: 999,
              background: 'rgba(255,250,240,0.92)',
              color: 'var(--amber-deep)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 10px rgba(0,0,0,0.28)',
            }}
          >
            <Icon name="play" size={16} />
          </span>
        ) : (
          !cover && (
            <span style={{ color: accent, opacity: 0.9 }}>
              <Icon name={kind.icon} size={26} />
            </span>
          )
        )}
        {variant === 'aha' && (
          <span style={{ position: 'absolute', top: 7, left: 7, color: '#C8923B' }}>
            <Icon name="sparkle" size={15} />
          </span>
        )}
      </div>

      {/* right text panel */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: '11px 14px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          gap: 5,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              minWidth: 0,
              color: accent,
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            <Icon name={kind.icon} size={12} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {kind.label}
              {metaText ? ` · ${metaText}` : ''}
              {locked ? ' · next in turn' : ''}
            </span>
          </span>
          <span style={{ flex: 1 }} />
          <FavouriteButton stepId={step.id} />
        </div>
        <Display parts={step.title} size={15.5} lh={1.16} />
      </div>
      {/* locked → soft grey veil (matches CoverTile) so it reads recessed on cream */}
      {locked && (
        <span
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, zIndex: 4, background: 'rgba(247,245,240,0.4)', pointerEvents: 'none' }}
        />
      )}
    </div>
  );
}
